import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, CircularProgress } from '@mui/material';

// Import services
import AuditServices from '../../services/audit.services';

// Import components
import ModuleUsageCalendar from './components/tables/moduleUsageCalendar';
import DateRangeSelector from './utils/DateRangeSelector';
import ModuleComparisonChart from './components/charts/ModuleComparisonChart';
import TopUsersWidgetGrid from './components/widgets/TopUsersWidget';
import UserStatsTable from './components/tables/UserStatsTable';
import { EnhancedUserComparisonChart } from './components/charts/UserComparisonChart';
import { DashboardSettingsProvider, useDashboardSettings } from './context/DashboardSettingsContext';
import logger from '../../utils/logger';

// Inner component to use the context
const UserActivityDashboard = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  
  // Use the global settings context
  const { globalSettings, updateGlobalSettings } = useDashboardSettings();
  const { startDate, endDate } = globalSettings;

  // Fetch data with date range
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const userdata = await AuditServices.getUserData(null, startDate, endDate);
        setData(userdata);
        
        // Extract unique modules
        if (userdata && userdata.length > 0) {
          const uniqueModules = new Set();
          userdata.forEach(entry => {
            entry.modules.forEach(module => {
              uniqueModules.add(module.module);
            });
          });
          setModules(Array.from(uniqueModules).sort());
        }
      } catch (error) {
        logger.error('Failed to fetch user activity data:', error);
        setData([]);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [startDate, endDate]);

  // Handle date range changes from selector
  const handleDateRangeChange = (start, end) => {
    updateGlobalSettings({
      startDate: start,
      endDate: end
    });
  };

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" sx={{ my: 3 }}>User Activity Dashboard</Typography>
      
      {/* Date Range Selector */}
      <DateRangeSelector 
        onDateRangeChange={handleDateRangeChange} 
        initialStartDate={startDate}
        initialEndDate={endDate}
      />
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Activity Calendar Summary (Formerly Raw Activity Table) */}
          <Box sx={{ mb: 4 }}>
            <ModuleUsageCalendar data={data} />
          </Box>
          
          {/* Module Comparison Chart - Enhanced version */}
          <Box sx={{ mb: 4 }}>
            <ModuleComparisonChart data={data} />
          </Box>
          
          {/* User Comparison Chart - Enhanced version with settings */}
          <Box sx={{ mb: 4 }}>
            <EnhancedUserComparisonChart />
          </Box>
          
          {/* Top Users by Module */}
          <TopUsersWidgetGrid 
            modules={modules} 
            startDate={startDate} 
            endDate={endDate} 
          />
          
          {/* Overall User Stats Table */}
          <UserStatsTable 
            startDate={startDate} 
            endDate={endDate} 
          />
        </>
      )}
    </Container>
  );
};

// Main component wrapped with settings provider
const UserActivity = () => {
  return (
    <DashboardSettingsProvider>
      <UserActivityDashboard />
    </DashboardSettingsProvider>
  );
};

export default UserActivity;
